import React, { useState, useEffect, useCallback } from "react";
import api from "../api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, BarChart3, RefreshCw, Save, DollarSign, Plus, Minus, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { value: "7", label: "Ultimi 7 giorni" },
  { value: "30", label: "Ultimi 30 giorni" },
  { value: "90", label: "Ultimi 90 giorni" },
  { value: "365", label: "Ultimo anno" },
  { value: "0", label: "Da sempre" },
];

const FUNC_LABELS = {
  polish: "Ottimizza con l'AI (Polish)",
  text_message: "Genera messaggio (Da testo)",
  split: "Dividi messaggio",
  simple_chat: "Chat semplificata",
  adapt_why: "Adatta vantaggi (Da testo)",
  extract: "Estrazione oggetti da testo",
  photo: "Analisi foto (visione)",
  cap: "Ricerca CAP / coordinate",
  travel: "Stima distanza e tempo",
  market: "Mercato self-storage (Oggetti)",
  box_rental: "Mercato box in affitto (Area)",
  self_storage: "Mercato self-storage (Area)",
  prices: "Stima prezzi oggetti",
  unknown: "Altro",
};

const usd = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const num = (n) => (Number(n) || 0).toLocaleString("it-IT");

export default function AiUsageStats({ isAdmin }) {
  const [days, setDays] = useState("30");
  const [uid, setUid] = useState("me");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingMeta, setPricingMeta] = useState({ last_checked: "", source: "" });
  const [savingPrice, setSavingPrice] = useState(false);
  const [checking, setChecking] = useState(false);
  const [expandedFunc, setExpandedFunc] = useState(null);
  const [funcDetails, setFuncDetails] = useState({}); // key -> { loading, rows }

  const load = useCallback(async () => {
    setLoading(true);
    setExpandedFunc(null);
    setFuncDetails({});
    try {
      const { data } = await api.get(`/ai/usage?days=${days}&uid=${encodeURIComponent(uid)}`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, uid]);

  useEffect(() => { load(); }, [load]);

  const loadPricing = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/pricing");
      setPricing(data.pricing || {});
      setPricingMeta({ last_checked: data.last_checked || "", source: data.source || "" });
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadPricing(); }, [loadPricing]);

  const setPrice = (m, field, val) => setPricing((p) => ({ ...p, [m]: { ...p[m], [field]: val } }));

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      const clean = {};
      Object.entries(pricing).forEach(([m, v]) => { clean[m] = { input: parseFloat(v.input) || 0, output: parseFloat(v.output) || 0 }; });
      await api.post("/ai/pricing", { pricing: clean });
      toast.success("Prezzi salvati");
    } catch { toast.error("Errore salvataggio prezzi"); }
    finally { setSavingPrice(false); }
  };

  const checkPricing = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/ai/pricing/refresh");
      setPricing(data.pricing || {});
      setPricingMeta((m) => ({ ...m, last_checked: data.checked_at || m.last_checked }));
      toast.success(data.matched > 0 ? `Prezzi aggiornati (${data.matched} modelli)` : "Controllo completato: nessun prezzo aggiornabile dalla pagina (mantieni i valori manuali)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile leggere i prezzi ora"); }
    finally { setChecking(false); }
  };

  const t = data?.total || { calls: 0, prompt_tokens: 0, completion_tokens: 0, total_tokens: 0, cost_usd: 0 };
  const users = data?.users || [];

  const toggleFunc = async (key) => {
    if (expandedFunc === key) { setExpandedFunc(null); return; }
    setExpandedFunc(key);
    if (!funcDetails[key]) {
      setFuncDetails((d) => ({ ...d, [key]: { loading: true, rows: [] } }));
      try {
        const { data } = await api.get(`/ai/usage/details?function=${encodeURIComponent(key)}&days=${days}&uid=${encodeURIComponent(uid)}`);
        setFuncDetails((d) => ({ ...d, [key]: { loading: false, rows: data.rows || [] } }));
      } catch {
        setFuncDetails((d) => ({ ...d, [key]: { loading: false, rows: [] } }));
      }
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  const quoteHref = (id) => `${window.location.origin}/?quote=${id}`;

  const Details = ({ state }) => {
    if (!state || state.loading) return <div className="p-3 text-center text-slate-400 text-xs flex items-center justify-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento richieste…</div>;
    if (!state.rows.length) return <div className="p-3 text-center text-slate-400 text-xs">Nessuna richiesta registrata.</div>;
    return (
      <div className="divide-y divide-slate-100">
        <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-1.5 bg-slate-50/60 text-[10px] uppercase tracking-wide text-slate-400">
          <span>Data e ora</span><span className="text-right">Token</span><span className="text-right">Stima</span>
        </div>
        {state.rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-1.5 text-xs items-center" data-testid={`usage-detail-row-${i}`}>
            <span className="text-slate-600">{fmtDate(r.created_at)}</span>
            <span className="text-right text-slate-500 tabular-nums">{num(r.total_tokens)}</span>
            <span className="text-right">
              {r.quote_id ? (
                <a href={quoteHref(r.quote_id)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[#1a5f5a] hover:text-[#134744] font-medium hover:underline"
                  data-testid={`usage-detail-stima-${i}`}>
                  #{r.quote_number ?? "?"} <ExternalLink className="h-3 w-3" />
                </a>
              ) : <span className="text-slate-300">—</span>}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const Table = ({ rows, labelFn, testid, expandable }) => (
    <div className="overflow-hidden rounded-md border border-slate-200" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
            <th className="text-left p-2.5 font-medium">Voce</th>
            <th className="text-right p-2.5 font-medium">Chiamate</th>
            <th className="text-right p-2.5 font-medium">Token</th>
            <th className="text-right p-2.5 font-medium">Costo stim.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={4} className="p-4 text-center text-slate-400 text-xs">Nessun dato nel periodo.</td></tr>
          ) : rows.map((r) => (
            <React.Fragment key={r.key}>
              <tr className={expandable ? "cursor-pointer hover:bg-slate-50" : ""} onClick={expandable ? () => toggleFunc(r.key) : undefined} data-testid={expandable ? `usage-func-row-${r.key}` : undefined}>
                <td className="p-2.5 text-slate-700">
                  {expandable ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-4 w-4 flex items-center justify-center rounded border border-slate-300 text-slate-500" data-testid={`usage-func-toggle-${r.key}`}>
                        {expandedFunc === r.key ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      </span>
                      {labelFn ? labelFn(r.key) : r.key}
                    </span>
                  ) : (labelFn ? labelFn(r.key) : r.key)}
                </td>
                <td className="p-2.5 text-right text-slate-500">{num(r.calls)}</td>
                <td className="p-2.5 text-right text-slate-500">{num(r.total_tokens)}</td>
                <td className="p-2.5 text-right font-medium text-[#1a5f5a]">{usd(r.cost_usd)}</td>
              </tr>
              {expandable && expandedFunc === r.key && (
                <tr data-testid={`usage-func-details-${r.key}`}>
                  <td colSpan={4} className="p-0 bg-white border-t border-slate-100">
                    <Details state={funcDetails[r.key]} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <section id="sec-statistiche" className="scroll-mt-36" data-testid="ai-usage-stats">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[#1a5f5a]" />
          <h3 className="font-display font-semibold text-lg">Statistiche AI</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select value={uid} onValueChange={setUid}>
            <SelectTrigger className="w-[190px] h-9" data-testid="usage-user-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="me" data-testid="usage-user-me">Solo me</SelectItem>
              <SelectItem value="all" data-testid="usage-user-all">Tutti gli utenti</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`usage-user-${u.id}`}>{u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[170px] h-9" data-testid="usage-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.value} value={p.value} data-testid={`usage-period-${p.value}`}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={load} disabled={loading} className="h-9 w-9 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="usage-refresh" title="Aggiorna">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-4">Consumo dei crediti AI e costo stimato per modello e per funzione. I costi sono stime basate sul listino OpenAI e possono differire dalla fatturazione reale. Il conteggio parte dal momento in cui è stato attivato il tracciamento.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Costo stimato</div>
          <div className="text-xl font-display font-semibold text-[#1a5f5a] mt-0.5" data-testid="usage-total-cost">{usd(t.cost_usd)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Chiamate</div>
          <div className="text-xl font-display font-semibold text-slate-700 mt-0.5" data-testid="usage-total-calls">{num(t.calls)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Token input</div>
          <div className="text-xl font-display font-semibold text-slate-700 mt-0.5">{num(t.prompt_tokens)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Token output</div>
          <div className="text-xl font-display font-semibold text-slate-700 mt-0.5">{num(t.completion_tokens)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-2">Per funzione</div>
          <Table rows={data?.byFunction || []} labelFn={(k) => FUNC_LABELS[k] || k} testid="usage-by-function" expandable />
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-2">Per modello</div>
          <Table rows={data?.byModel || []} testid="usage-by-model" />
        </div>
      </div>

      {uid === "all" && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-500 mb-2">Per utente</div>
          <Table rows={(data?.byUser || []).map((r) => ({ ...r, key: r.label || r.key }))} testid="usage-by-user" />
        </div>
      )}

      {isAdmin && pricing && (
        <div className="mt-6 pt-5 border-t border-slate-200" data-testid="ai-pricing-editor">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-[#1a5f5a]" />
              <h4 className="font-display font-semibold">Prezzi modelli (USD / 1M token)</h4>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={checkPricing} disabled={checking} className="gap-1.5 h-8" data-testid="pricing-check">
                {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Controlla ora
              </Button>
              <Button size="sm" onClick={savePricing} disabled={savingPrice} className="gap-1.5 h-8 bg-[#1a5f5a] hover:bg-[#134744]" data-testid="pricing-save">
                {savingPrice ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salva prezzi
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mb-3">
            Usati per stimare i costi. Controllo automatico settimanale dalla pagina prezzi OpenAI; se non riesce a leggere un modello mantiene il valore manuale.
            {pricingMeta.last_checked && <> Ultimo controllo: <b>{pricingMeta.last_checked.slice(0, 10)}</b>.</>}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.keys(pricing).sort().map((m) => (
              <div key={m} className="border border-slate-200 rounded-md p-2.5" data-testid={`pricing-row-${m}`}>
                <div className="text-xs font-medium text-slate-700 mb-1.5 truncate">{m}</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-400">Input</label>
                    <Input type="number" step="0.01" value={pricing[m]?.input ?? ""} onChange={(e) => setPrice(m, "input", e.target.value)} className="h-8 text-sm" data-testid={`pricing-input-${m}`} />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-400">Output</label>
                    <Input type="number" step="0.01" value={pricing[m]?.output ?? ""} onChange={(e) => setPrice(m, "output", e.target.value)} className="h-8 text-sm" data-testid={`pricing-output-${m}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
